import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { pipelineApi, userApi } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import toast from 'react-hot-toast';
import {
  SparklesIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  BuildingOffice2Icon,
  WrenchScrewdriverIcon,
  PlusCircleIcon,
} from 'components/icons';
import { CheckCircleIcon as CheckCircleSolidIcon } from 'components/icons/solid';
import NaicsCodePicker from '../ui/NaicsCodePicker';
import { SET_ASIDE_TYPES } from '../../data/govconData';

interface ClassificationSuggestion {
  suggested: string;
  confidence: string;
  reasoning: string;
  naicsCode: string;
  naicsDescription: string;
  requirementSummary: string;
  canUserFulfill: boolean;
}

interface ProfileReadiness {
  profileComplete?: boolean;
  ready: boolean;
  classification: string;
  missingFields: { field: string; label: string; required: boolean; fixableInClevor?: boolean }[];
  requiredMissing: number;
}

interface Props {
  opportunityId: string;
  onClassified: (classification: string) => void;
  onProfileIncomplete: (missingFields: any[]) => void;
}

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME',
  'MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI',
  'SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
];

// Set-aside options come from the shared govcon data, not a local list. This
// was a hardcoded five-entry array that had already drifted from
// SET_ASIDE_TYPES — missing VOSB, SDB, SB and others a bidder can legitimately
// hold, so a qualifying company could not record the certification that makes
// it eligible. "None" is appended because it is a real answer: without it,
// having no certifications is indistinguishable from not having answered.
const CERT_OPTIONS = [...SET_ASIDE_TYPES.map(t => t.code), 'None of these'];

const InlineProfileCollection: React.FC<{
  profileReadiness: ProfileReadiness;
  opportunityId: string;
  onComplete: () => void;
}> = ({ profileReadiness, opportunityId, onComplete }) => {
  const [formData, setFormData] = useState<Record<string, string | string[]>>({});
  const [saving, setSaving] = useState(false);

  // Only fields this form can collect are enforced. The SAM registration gap is
  // required in the sense that matters — a quote can be found non-responsive
  // without it — but there is no input for it here, so demanding it would trap
  // the user on a screen they cannot satisfy.
  const requiredFields = profileReadiness.missingFields.filter(
    f => f.required && f.fixableInClevor !== false,
  );
  // Items that block a bid but have to be resolved outside Clevor. Shown, never
  // demanded — there is no input here that could satisfy them.
  const externalBlockers = profileReadiness.missingFields.filter(
    f => f.required && f.fixableInClevor === false,
  );
  const optionalFields = profileReadiness.missingFields.filter(
    f => !f.required || f.fixableInClevor === false,
  );
  const allFields = [...requiredFields, ...optionalFields];
  const filledCount = allFields.filter(f => {
    const val = formData[f.field];
    return val && (Array.isArray(val) ? val.length > 0 : val.trim() !== '');
  }).length;

  const handleSave = async () => {
    // Check all required fields are filled
    for (const field of requiredFields) {
      const val = formData[field.field];
      if (!val || (Array.isArray(val) ? val.length === 0 : val.trim() === '')) {
        toast.error(`${field.label} is required`);
        return;
      }
    }

    setSaving(true);
    try {
      await userApi.updateMe(formData);
      toast.success('Profile updated');
      onComplete();
    } catch {
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const renderField = (field: { field: string; label: string; required: boolean }) => {
    const value = formData[field.field] || '';

    if (field.field === 'companyState') {
      return (
        <select
          value={value as string}
          onChange={(e) => setFormData(prev => ({ ...prev, [field.field]: e.target.value }))}
          className="w-full border border-border-strong rounded-lg px-3 py-2 text-sm focus:ring-brand-500 focus:border-brand-500"
        >
          <option value="">Select state...</option>
          {US_STATES.map(st => <option key={st} value={st}>{st}</option>)}
        </select>
      );
    }

    if (field.field === 'certifications') {
      const selected = (formData.certifications || []) as string[];
      return (
        <div className="flex flex-wrap gap-2">
          {CERT_OPTIONS.map(cert => (
            <button
              key={cert}
              type="button"
              onClick={() => {
                const next = selected.includes(cert)
                  ? selected.filter(c => c !== cert)
                  : [...selected, cert];
                setFormData(prev => ({ ...prev, certifications: next }));
              }}
              title={SET_ASIDE_TYPES.find(t => t.code === cert)?.label || cert}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                selected.includes(cert)
                  ? 'bg-brand-100 border-brand-300 text-brand-700'
                  : 'bg-surface-sunk border-border text-content-muted hover:bg-surface-sunk'
              }`}
            >
              {cert}
            </button>
          ))}
        </div>
      );
    }

    if (field.field === 'naicsCodes') {
      const selected = (formData.naicsCodes || []) as string[];
      return (
        <NaicsCodePicker
          value={selected}
          onChange={(codes) => setFormData(prev => ({ ...prev, naicsCodes: codes }))}
        />
      );
    }

    if (field.field === 'companySize') {
      return (
        <select
          value={value as string}
          onChange={(e) => setFormData(prev => ({ ...prev, [field.field]: e.target.value }))}
          className="w-full border border-border-strong rounded-lg px-3 py-2 text-sm focus:ring-brand-500 focus:border-brand-500"
        >
          <option value="">Select size...</option>
          <option value="SOLO">Solo (1 person)</option>
          <option value="SMALL">Small (2-10)</option>
          <option value="MEDIUM">Medium (11-50)</option>
          <option value="LARGE">Large (51-500)</option>
          <option value="ENTERPRISE">Enterprise (500+)</option>
        </select>
      );
    }

    // Default text input
    const placeholders: Record<string, string> = {
      companyName: 'Your Company LLC',
      cageCode: 'e.g., 7ABC1',
      ueiSam: '12-character UEI',
      companyCity: 'City name',
      industry: 'e.g., IT Services',
    };

    return (
      <input
        type="text"
        value={value as string}
        onChange={(e) => setFormData(prev => ({ ...prev, [field.field]: e.target.value }))}
        placeholder={placeholders[field.field] || ''}
        className="w-full border border-border-strong rounded-lg px-3 py-2 text-sm focus:ring-brand-500 focus:border-brand-500"
      />
    );
  };

  return (
    <div className="bg-surface shadow rounded-lg overflow-hidden">
      <div className="px-6 py-5 border-b border-amber-200 bg-amber-50">
        <div className="flex items-center gap-3">
          <ExclamationTriangleIcon className="h-6 w-6 text-amber-600" />
          <div>
            <h3 className="font-semibold text-content">A Few Details Needed</h3>
            <p className="text-sm text-amber-700 mt-0.5">
              Fill these in to generate your proposal. {filledCount}/{allFields.length} complete.
            </p>
          </div>
        </div>
      </div>
      <div className="p-6 space-y-4">
        {allFields.map((field) => {
          // Rendered as guidance, not an input: this is something the user has
          // to do on SAM.gov, and Clevor cannot do it for them or verify it.
          if (field.field === 'samNaicsRegistration') {
            return (
              <div
                key={field.field}
                className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm"
              >
                <p className="font-medium text-amber-900">{field.label}</p>
                <p className="text-amber-800 mt-1">
                  Your SAM.gov registration lists the NAICS codes your business is registered
                  under, and a quote is evaluated against the solicitation's code — including the
                  small-business size standard that decides set-aside eligibility. If this code
                  isn't on your registration, the offer can be found non-responsive.
                </p>
                <a
                  href="https://sam.gov/content/entity-registration"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-2 font-medium text-amber-900 underline"
                >
                  Update your SAM registration →
                </a>
              </div>
            );
          }
          return (
            <div key={field.field}>
              <label className="block text-sm font-medium text-content mb-1">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              {renderField(field)}
            </div>
          );
        })}

        {/* Continuing is allowed with an unresolved SAM registration gap —
            there is no input here that could fix it — but the button should not
            imply everything is settled. Naming it on the way past is the last
            chance to say so before someone spends a week on the bid. */}
        {externalBlockers.length > 0 && !saving && (
          <p className="text-xs text-amber-800 text-center">
            You can carry on drafting, but {externalBlockers.length === 1 ? 'this' : 'these'} must
            be resolved on SAM.gov before you submit.
          </p>
        )}

        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-brand-600 text-white rounded-lg hover:bg-brand-700 text-sm font-medium disabled:opacity-50"
        >
          {saving ? (
            <><ArrowPathIcon className="h-4 w-4 animate-spin" /> Saving...</>
          ) : (
            <>Continue to Proposal</>
          )}
        </button>

        <p className="text-xs text-content-subtle text-center">
          Or <Link to={`/settings?tab=profile&returnTo=${encodeURIComponent(`/proposal-writer?opportunityId=${opportunityId}`)}`} className="text-brand-600 hover:text-brand-500">go to full Company Profile</Link>
        </p>
      </div>
    </div>
  );
};

const ClassificationGate: React.FC<Props> = ({ opportunityId, onClassified, onProfileIncomplete }) => {
  const { user, refreshUser } = useAuth();

  // The profile records how this company usually pursues work. It is a default,
  // not an answer: someone who can self-perform may still choose to prime a
  // given job with subs, so the question is always asked — but recommending
  // "fulfill it directly" to a declared generalist, as this did for everyone,
  // is worse than not recommending at all.
  const profilePosture = (user as any)?.contractorType as
    | 'direct_provider'
    | 'generalist_contractor'
    | 'both'
    | undefined;
  const [step, setStep] = useState<'loading' | 'question1' | 'question2' | 'profile_check' | 'done'>('loading');
  const [suggestion, setSuggestion] = useState<ClassificationSuggestion | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profileReadiness, setProfileReadiness] = useState<ProfileReadiness | null>(null);
  const [, setSelectedClassification] = useState<string | null>(null);
  const [, setNaicsAdded] = useState(false);
  const [addingNaics, setAddingNaics] = useState(false);

  const handleAddNaicsCode = async (code: string) => {
    setAddingNaics(true);
    try {
      const currentCodes = user?.naicsCodes || [];
      if (currentCodes.includes(code)) {
        setNaicsAdded(true);
        return;
      }
      await userApi.updateMe({ naicsCodes: [...currentCodes, code] });
      await refreshUser();
      setNaicsAdded(true);
      // Update the suggestion to reflect the match
      if (suggestion) {
        setSuggestion({ ...suggestion, canUserFulfill: true });
      }
      toast.success(`NAICS ${code} added to your profile`);
    } catch {
      toast.error('Failed to add NAICS code');
    } finally {
      setAddingNaics(false);
    }
  };

  useEffect(() => {
    checkExistingClassification();
  // Keyed on opportunityId. checkExistingClassification is redefined every render, so listing it would re-check on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opportunityId]);

  const checkExistingClassification = async () => {
    try {
      const response = await pipelineApi.getClassification(opportunityId);
      const data = response.data.data;

      if (data.classification && data.confirmed) {
        // Already classified — skip straight to profile check, no AI call needed
        await checkProfile(data.classification);
        return;
      }
    } catch {
      // Classification endpoint failed — might be 404, that's ok
    }

    // Only run AI suggestion if no confirmed classification exists
    getSuggestion();
  };

  const getSuggestion = async () => {
    setAnalyzing(true);
    try {
      const response = await pipelineApi.suggestClassification(opportunityId);
      const data = response.data.data;
      setSuggestion(data);
      setStep('question1');
    } catch (err: any) {
      // If AI fails, show question1 without suggestion
      setSuggestion(null);
      setStep('question1');
      toast.error('Could not analyze solicitation NAICS — please answer manually');
    } finally {
      setAnalyzing(false);
    }
  };

  // Question 1: Can you directly fulfill this?
  const handleQuestion1 = async (canFulfill: boolean) => {
    if (canFulfill) {
      // They CAN do it — ask if they want to do it directly or prime it
      setStep('question2');
    } else {
      // They CANNOT do it — only option is generalist contractor
      await saveAndCheckProfile('generalist_contractor');
    }
  };

  // Question 2: How do you want to pursue? (only shown if they CAN fulfill)
  const handleQuestion2 = async (choice: 'direct_provider' | 'generalist_contractor') => {
    await saveAndCheckProfile(choice);
  };

  const saveAndCheckProfile = async (classification: string) => {
    setSaving(true);
    setSelectedClassification(classification);
    try {
      await pipelineApi.saveClassification(opportunityId, classification);
      await checkProfile(classification);
    } catch (err: any) {
      toast.error('Failed to save classification');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Decide whether to show the profile screen.
   *
   * `afterSave` is the asymmetry that matters. On the way in we gate on
   * `ready` — a user carrying a SAM.gov NAICS gap is not cleared to bid, and
   * this screen is the only place in the flow that says so. Gating on profile
   * completeness alone is what let someone fill in every field and never see
   * the one warning that could have their quote thrown out.
   *
   * But `ready` cannot become true by filling in this form: the SAM gap is
   * fixed on SAM.gov, not here. So once they have been shown it and completed
   * everything the form can collect, they move on. Re-gating on `ready` after a
   * save would leave them on a screen with no exit.
   */
  const checkProfile = async (classification: string, afterSave = false) => {
    try {
      const response = await pipelineApi.getProfileReadiness(opportunityId);
      const data = response.data.data;

      const canProceed = afterSave
        ? data.profileComplete !== false
        : data.ready;

      if (canProceed) {
        setStep('done');
        onClassified(classification);
      } else {
        setProfileReadiness(data);
        setStep('profile_check');
        onProfileIncomplete(data.missingFields);
      }
    } catch {
      setStep('done');
      onClassified(classification);
    }
  };

  // Loading / analyzing state
  if (step === 'loading' || analyzing) {
    return (
      <div className="bg-surface shadow rounded-lg p-8 text-center">
        <SparklesIcon className="h-8 w-8 text-brand-600 animate-pulse mx-auto" />
        <p className="mt-3 text-sm text-content-muted">Analyzing solicitation requirements...</p>
        <p className="mt-1 text-xs text-content-subtle">Reviewing NAICS code and scope of work</p>
      </div>
    );
  }

  // Done
  if (step === 'done') return null;

  // Profile incomplete — inline collection
  if (step === 'profile_check' && profileReadiness) {
    return (
      <InlineProfileCollection
        profileReadiness={profileReadiness}
        opportunityId={opportunityId}
        onComplete={async () => {
          await checkProfile(profileReadiness.classification, true);
        }}
      />
    );
  }

  // QUESTION 1: Can your company directly fulfill this requirement?
  if (step === 'question1') {
    return (
      <div className="bg-surface shadow rounded-lg overflow-hidden">
        <div className="px-6 py-5 border-b border-border">
          <h3 className="font-semibold text-content">Solicitation Classification</h3>
          <p className="text-sm text-content-muted mt-1">
            Based on the NAICS code and requirements, we need to determine how you'll pursue this contract.
          </p>
        </div>

        {/* NAICS Analysis */}
        {suggestion && (
          <div className="px-6 pt-4">
            <div className="p-4 bg-surface-sunk rounded-lg border border-border">
              <div className="flex items-start gap-2">
                <SparklesIcon className="h-5 w-5 text-brand-600 flex-shrink-0 mt-0.5" />
                <div>
                  {suggestion.naicsCode && (
                    <p className="text-sm font-medium text-content">
                      NAICS {suggestion.naicsCode}: {suggestion.naicsDescription}
                    </p>
                  )}
                  <p className="text-sm text-content-muted mt-1">{suggestion.requirementSummary}</p>
                  {suggestion.canUserFulfill && (
                    <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                      <CheckCircleSolidIcon className="h-3.5 w-3.5" />
                      Your company's NAICS codes match this solicitation
                    </p>
                  )}
                  {!suggestion.canUserFulfill && suggestion.naicsCode && (
                    <div className="mt-2">
                      <p className="text-xs text-amber-600 flex items-center gap-1">
                        <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                        Your company's NAICS codes don't include {suggestion.naicsCode}
                      </p>
                      <button
                        type="button"
                        onClick={() => handleAddNaicsCode(suggestion.naicsCode)}
                        disabled={addingNaics}
                        className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-500 disabled:opacity-50"
                      >
                        {addingNaics ? (
                          <><ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> Adding...</>
                        ) : (
                          <><PlusCircleIcon className="h-3.5 w-3.5" /> Add NAICS {suggestion.naicsCode} to my profile</>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* The Question */}
        <div className="px-6 pt-5 pb-2">
          <p className="text-base font-medium text-content">
            {suggestion?.requirementSummary
              ? `Can your company directly ${suggestion.requirementSummary.toLowerCase().startsWith('provide') || suggestion.requirementSummary.toLowerCase().startsWith('perform') || suggestion.requirementSummary.toLowerCase().startsWith('manufacture') || suggestion.requirementSummary.toLowerCase().startsWith('supply')
                  ? suggestion.requirementSummary.toLowerCase()
                  : 'fulfill: ' + suggestion.requirementSummary.toLowerCase()}?`
              : 'Can your company directly fulfill this solicitation\'s requirements?'
            }
          </p>
        </div>

        {/* Yes / No Options */}
        <div className="p-6 space-y-3">
          <button
            onClick={() => handleQuestion1(true)}
            disabled={saving}
            className="w-full text-left p-4 rounded-lg border-2 border-green-300 bg-green-50 hover:border-green-500 hover:bg-green-100 transition-all"
          >
            <div className="flex items-start gap-3">
              <WrenchScrewdriverIcon className="h-6 w-6 flex-shrink-0 mt-0.5 text-green-600" />
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-content">Yes, my company can do this</span>
                  {suggestion?.canUserFulfill && (
                    <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full font-medium">
                      NAICS Match
                    </span>
                  )}
                </div>
                <p className="text-sm text-content-muted mt-0.5">
                  We have the capability, staff, equipment, or products to directly fulfill this requirement.
                </p>
              </div>
            </div>
          </button>

          <button
            onClick={() => handleQuestion1(false)}
            disabled={saving}
            className="w-full text-left p-4 rounded-lg border-2 border-blue-300 bg-blue-50 hover:border-blue-500 hover:bg-blue-100 transition-all"
          >
            <div className="flex items-start gap-3">
              <BuildingOffice2Icon className="h-6 w-6 flex-shrink-0 mt-0.5 text-blue-600" />
              <div>
                <span className="font-medium text-content">No, I need to assemble a team</span>
                <p className="text-sm text-content-muted mt-0.5">
                  I'll act as a prime contractor and find subcontractors, vendors, or teaming partners to fulfill the work.
                </p>
              </div>
            </div>
          </button>
        </div>

        {saving && (
          <div className="px-6 pb-4 text-center">
            <ArrowPathIcon className="h-5 w-5 text-brand-600 animate-spin mx-auto" />
            <p className="text-xs text-content-muted mt-1">Saving...</p>
          </div>
        )}
      </div>
    );
  }

  // QUESTION 2: Direct provider or prime with subs? (only if they CAN fulfill)
  if (step === 'question2') {
    return (
      <div className="bg-surface shadow rounded-lg overflow-hidden">
        <div className="px-6 py-5 border-b border-border">
          <h3 className="font-semibold text-content">How Do You Want to Pursue This Contract?</h3>
          <p className="text-sm text-content-muted mt-1">
            You have the capability to fulfill this directly. Choose how you want to approach this bid.
          </p>
          {/* Say where the suggestion comes from. An unexplained "Recommended"
              badge reads as the system guessing; naming the profile makes it a
              default the user set and can change. */}
          {profilePosture && profilePosture !== 'both' && (
            <p className="text-xs text-content-subtle mt-1.5">
              Your{' '}
              <Link to="/settings" className="underline hover:text-content">
                company profile
              </Link>{' '}
              says you usually{' '}
              {profilePosture === 'direct_provider'
                ? 'perform the work yourself'
                : 'bring in subs or partners'}
              . This bid is your call — pick either.
            </p>
          )}
        </div>

        <div className="p-6 space-y-3">
          <button
            onClick={() => handleQuestion2('direct_provider')}
            disabled={saving}
            className="w-full text-left p-4 rounded-lg border-2 border-green-300 bg-green-50 hover:border-green-500 hover:bg-green-100 transition-all"
          >
            <div className="flex items-start gap-3">
              <WrenchScrewdriverIcon className="h-6 w-6 flex-shrink-0 mt-0.5 text-green-600" />
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-content">Fulfill it directly</span>
                  {profilePosture === 'direct_provider' && (
                    <span className="text-xs px-1.5 py-0.5 bg-brand-100 text-brand-700 rounded-full font-medium">
                      Your usual approach
                    </span>
                  )}
                </div>
                <p className="text-sm text-content-muted mt-0.5">
                  My company will perform the work or supply the product using our own resources and capabilities.
                </p>
                <p className="text-xs text-content-subtle mt-1">
                  Streamlined checklist focused on proving your direct capability, past performance, and pricing.
                </p>
              </div>
            </div>
          </button>

          <button
            onClick={() => handleQuestion2('generalist_contractor')}
            disabled={saving}
            className="w-full text-left p-4 rounded-lg border-2 border-blue-300 bg-blue-50 hover:border-blue-500 hover:bg-blue-100 transition-all"
          >
            <div className="flex items-start gap-3">
              <BuildingOffice2Icon className="h-6 w-6 flex-shrink-0 mt-0.5 text-blue-600" />
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-content">Act as prime contractor with subcontractors</span>
                  {profilePosture === 'generalist_contractor' && (
                    <span className="text-xs px-1.5 py-0.5 bg-brand-100 text-brand-700 rounded-full font-medium">
                      Your usual approach
                    </span>
                  )}
                </div>
                <p className="text-sm text-content-muted mt-0.5">
                  I want to manage the contract and bring in subcontractors or teaming partners for execution.
                </p>
                <p className="text-xs text-content-subtle mt-1">
                  Full checklist including vendor sourcing, teaming agreements, sub qualification, and coordination planning.
                </p>
              </div>
            </div>
          </button>
        </div>

        {saving && (
          <div className="px-6 pb-4 text-center">
            <ArrowPathIcon className="h-5 w-5 text-brand-600 animate-spin mx-auto" />
            <p className="text-xs text-content-muted mt-1">Saving...</p>
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default ClassificationGate;
